import { c as _c } from "react/compiler-runtime";
import { Badge, Box, Button, Card, Heading, HStack, Icon, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import { useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import { advisorProfileCompletenessItems, advisorProfileCompletenessScore } from '@src/components/sco/Advisors/utils/advisorProfileCompleteness';
import { advisorSupplyReadinessScore, advisorSupplyReadinessSteps, type AdvisorSupplyReadinessInput, type SupplyReadinessStep } from '@src/components/sco/Advisors/utils/advisorSupplyReadiness';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { LuCalendarClock, LuCheck, LuChevronRight, LuGlobe, LuUserRound, LuWallet } from 'react-icons/lu';
type AdvisorCompletenessCardProps = {
  profile: AdvisorSupplyReadinessInput | null | undefined;
  pictureUrl?: string | null;
  responsesCount?: number | null;
  foundersHelpedCount?: number | null;
};
const STEP_ICONS = {
  live: LuGlobe,
  complete: LuUserRound,
  rates: LuCalendarClock,
  connect: LuWallet
} as const;
const STEP_EDIT_LABEL: Partial<Record<SupplyReadinessStep['key'], string>> = {
  live: 'View Profile',
  complete: 'Edit Profile',
  rates: 'Edit Rates',
  connect: 'Edit Payouts'
};
const hrefFor = (paths: ReturnType<typeof useAdvisorsPaths>, step: SupplyReadinessStep) => {
  if (step.hrefKey === 'sessionSettings') {
    return paths.profileSettings;
  }
  if (step.hrefKey === 'payouts') {
    return paths.profilePayouts;
  }
  if (step.key === 'live') {
    return paths.profileView;
  }
  return paths.profileEdit;
};
export const AdvisorCompletenessCard = t0 => {
  const $ = _c(58);
  const {
    profile,
    pictureUrl,
    responsesCount,
    foundersHelpedCount
  } = t0;
  const paths = useAdvisorsPaths();
  let t1;
  if ($[0] !== pictureUrl || $[1] !== profile) {
    const items = advisorProfileCompletenessItems(profile, {
      pictureUrl
    });
    t1 = advisorProfileCompletenessScore(items);
    $[0] = pictureUrl;
    $[1] = profile;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    percent: profilePercent
  } = t1;
  let T0;
  let T1;
  let T2;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[3] !== foundersHelpedCount || $[4] !== paths || $[5] !== pictureUrl || $[6] !== profile || $[7] !== profilePercent || $[8] !== responsesCount) {
    const steps = advisorSupplyReadinessSteps(profile, {
      pictureUrl
    });
    const {
      done,
      total,
      percent
    } = advisorSupplyReadinessScore(steps);
    const ready = percent >= 100;
    const remaining = Math.max(0, total - done);
    const nextKey = steps.find(_temp)?.key;
    const showStats = (foundersHelpedCount ?? 0) > 0 || (responsesCount ?? 0) > 0;
    T2 = Card.Root;
    t8 = "hidden";
    t9 = "subtle";
    T1 = Card.Body;
    t5 = 5;
    const t10 = ready ? "You're ready to advise" : "Get ready to advise";
    let t11;
    if ($[20] !== t10) {
      t11 = <Heading size="lg">{t10}</Heading>;
      $[20] = t10;
      $[21] = t11;
    } else {
      t11 = $[21];
    }
    const t12 = ready ? "Your profile, session rates, and payouts are in place. Founders can book you." : "Finish the open step, then Connect when you want to withdraw.";
    let t13;
    if ($[22] !== t12) {
      t13 = <Text color="colorPalette.fg/60" textStyle="sm">{t12}</Text>;
      $[22] = t12;
      $[23] = t13;
    } else {
      t13 = $[23];
    }
    let t14;
    if ($[24] !== done || $[25] !== profilePercent || $[26] !== total) {
      t14 = <Text color="colorPalette.fg/50" textStyle="xs">{done} of {total} ready · profile {profilePercent}% complete</Text>;
      $[24] = done;
      $[25] = profilePercent;
      $[26] = total;
      $[27] = t14;
    } else {
      t14 = $[27];
    }
    let t15;
    if ($[28] !== t11 || $[29] !== t13 || $[30] !== t14) {
      t15 = <VStack align="start" gap={1} minWidth={0}>{t11}{t13}{t14}</VStack>;
      $[28] = t11;
      $[29] = t13;
      $[30] = t14;
      $[31] = t15;
    } else {
      t15 = $[31];
    }
    const t16 = ready ? "green" : "orange";
    const t17 = ready ? "All set" : `${remaining} left`;
    let t18;
    if ($[32] !== t16 || $[33] !== t17) {
      t18 = <Badge colorPalette={t16} flexShrink={0} variant="subtle">{t17}</Badge>;
      $[32] = t16;
      $[33] = t17;
      $[34] = t18;
    } else {
      t18 = $[34];
    }
    if ($[35] !== t15 || $[36] !== t18) {
      t6 = <HStack align="start" gap={3} justify="space-between" wrap="wrap">{t15}{t18}</HStack>;
      $[35] = t15;
      $[36] = t18;
      $[37] = t6;
    } else {
      t6 = $[37];
    }
    if ($[38] !== foundersHelpedCount || $[39] !== responsesCount || $[40] !== showStats) {
      t7 = showStats && <SimpleGrid columns={{
        base: 1,
        sm: 2
      }} gap={2}>{(foundersHelpedCount ?? 0) > 0 && <StatTile label="Founders helped" value={foundersHelpedCount} />}{(responsesCount ?? 0) > 0 && <StatTile label="Responses" value={responsesCount} />}</SimpleGrid>;
      $[38] = foundersHelpedCount;
      $[39] = responsesCount;
      $[40] = showStats;
      $[41] = t7;
    } else {
      t7 = $[41];
    }
    T0 = VStack;
    t2 = "stretch";
    t3 = 2;
    t4 = steps.map(step_0 => <StepRow key={step_0.key} href={hrefFor(paths, step_0)} isNext={!ready && step_0.key === nextKey} step={step_0} />);
    $[3] = foundersHelpedCount;
    $[4] = paths;
    $[5] = pictureUrl;
    $[6] = profile;
    $[7] = profilePercent;
    $[8] = responsesCount;
    $[9] = T0;
    $[10] = T1;
    $[11] = T2;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    T0 = $[9];
    T1 = $[10];
    T2 = $[11];
    t2 = $[12];
    t3 = $[13];
    t4 = $[14];
    t5 = $[15];
    t6 = $[16];
    t7 = $[17];
    t8 = $[18];
    t9 = $[19];
  }
  let t10;
  if ($[42] !== T0 || $[43] !== t2 || $[44] !== t3 || $[45] !== t4) {
    t10 = <T0 align={t2} gap={t3}>{t4}</T0>;
    $[42] = T0;
    $[43] = t2;
    $[44] = t3;
    $[45] = t4;
    $[46] = t10;
  } else {
    t10 = $[46];
  }
  let t11;
  if ($[47] !== T1 || $[48] !== t10 || $[49] !== t5 || $[50] !== t6 || $[51] !== t7) {
    t11 = <T1 gap={t5}>{t6}{t7}{t10}</T1>;
    $[47] = T1;
    $[48] = t10;
    $[49] = t5;
    $[50] = t6;
    $[51] = t7;
    $[52] = t11;
  } else {
    t11 = $[52];
  }
  let t12;
  if ($[53] !== T2 || $[54] !== t11 || $[55] !== t8 || $[56] !== t9) {
    t12 = <T2 overflow={t8} variant={t9}>{t11}</T2>;
    $[53] = T2;
    $[54] = t11;
    $[55] = t8;
    $[56] = t9;
    $[57] = t12;
  } else {
    t12 = $[57];
  }
  return t12;
};
const StatTile = t0 => {
  const $ = _c(7);
  const {
    value,
    label
  } = t0;
  let t1;
  if ($[0] !== value) {
    t1 = <Text fontWeight="semibold" letterSpacing="tight" textStyle="xl">{value}</Text>;
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label) {
    t2 = <Text color="colorPalette.fg/50" textStyle="xs">{label}</Text>;
    $[2] = label;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <Box bg="colorPalette.card/20" borderRadius="l2" paddingX={4} paddingY={3}>{t1}{t2}</Box>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
const StepRow = t0 => {
  const $ = _c(42);
  const {
    step,
    href,
    isNext
  } = t0;
  const StepIcon = STEP_ICONS[step.key];
  const editLabel = STEP_EDIT_LABEL[step.key];
  const iconBg = step.done ? "green.subtle" : step.warning ? "orange.subtle" : "colorPalette.muted/60";
  const iconFg = step.done ? "green.fg" : step.warning ? "orange.fg" : "colorPalette.fg/70";
  const borderColor = step.warning ? "orange.muted" : step.done ? "colorPalette.border/40" : isNext ? "colorPalette.border" : "colorPalette.border/60";
  let t1;
  if ($[0] !== StepIcon) {
    t1 = <Icon boxSize={5}><StepIcon /></Icon>;
    $[0] = StepIcon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== iconBg || $[3] !== iconFg || $[4] !== t1) {
    t2 = <Box alignItems="center" bg={iconBg} borderRadius="l2" color={iconFg} display="flex" flexShrink={0} height={10} justifyContent="center" width={10}>{t1}</Box>;
    $[2] = iconBg;
    $[3] = iconFg;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== step.label) {
    t3 = <Text color="colorPalette.fg" fontWeight="medium">{step.label}</Text>;
    $[6] = step.label;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== step.optional) {
    t4 = step.optional && <Badge size="sm" variant="outline">Optional</Badge>;
    $[8] = step.optional;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t3 || $[11] !== t4) {
    t5 = <HStack gap={2} wrap="wrap">{t3}{t4}</HStack>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== step.detail) {
    t6 = step.detail && <Text color="colorPalette.fg/50" textStyle="xs">{step.detail}</Text>;
    $[13] = step.detail;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== step.warning) {
    t7 = step.warning && <Text color="orange.fg" textStyle="xs">{step.warning}</Text>;
    $[15] = step.warning;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t5 || $[18] !== t6 || $[19] !== t7) {
    t8 = <VStack align="start" flex={1} gap={0.5} minWidth={0}>{t5}{t6}{t7}</VStack>;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== editLabel || $[22] !== href || $[23] !== isNext || $[24] !== step.done) {
    t9 = step.done ? <HStack flexShrink={0} gap={2}>{editLabel && <Button asChild={true} size="xs" variant="ghost"><NextLink href={href}>{editLabel}</NextLink></Button>}<Box color="green.fg"><Icon boxSize={5}><LuCheck /></Icon></Box></HStack> : <HStack color="colorPalette.fg/50" flexShrink={0} gap={1}>{isNext && <Text fontWeight="medium" textStyle="xs">Continue</Text>}<Icon boxSize={4}><LuChevronRight /></Icon></HStack>;
    $[21] = editLabel;
    $[22] = href;
    $[23] = isNext;
    $[24] = step.done;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== t2 || $[27] !== t8 || $[28] !== t9) {
    t10 = <HStack align="center" gap={3} padding={3}>{t2}{t8}{t9}</HStack>;
    $[26] = t2;
    $[27] = t8;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  const body = t10;
  const t11 = !step.done;
  const t12 = isNext ? "colorPalette.solid/50" : "colorPalette.card/40";
  const t13 = step.warning ? "orange.border" : "colorPalette.border";
  let t14;
  if ($[30] !== t13) {
    t14 = {
      bg: "colorPalette.card/70",
      borderColor: t13,
      textDecoration: "none"
    };
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== body || $[33] !== href || $[34] !== step.done) {
    t15 = step.done ? body : <NextLink href={href}>{body}</NextLink>;
    $[32] = body;
    $[33] = href;
    $[34] = step.done;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== borderColor || $[37] !== t11 || $[38] !== t12 || $[39] !== t14 || $[40] !== t15) {
    t16 = <Box asChild={t11} bg={t12} borderColor={borderColor} borderRadius="l2" borderWidth="1px" color="inherit" display="block" textDecoration="none" transitionDuration="moderate" transitionProperty="common" width="full" _hover={t14}>{t15}</Box>;
    $[36] = borderColor;
    $[37] = t11;
    $[38] = t12;
    $[39] = t14;
    $[40] = t15;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  return t16;
};
function _temp(step) {
  return !step.done;
}